import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle } from "lucide-react";
import AnslagGuideBox from "@/components/AnslagGuideBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUBRIKER = ["Hjälp i vardagen", "Hand mot hand", "Köp & sälj lokalt", "Evenemang"];

export default function SkickaAnslag() {
  const [searchParams] = useSearchParams();
  const preRubrik = searchParams.get("rubrik") || "";
  // Build 96.14 — CTA-typ från startsidan (?typ=karta|bygdepuls|anslag). Bara UI-kontext,
  // ingen backend-ändring. Vi visar en liten info-banner högst upp så användaren förstår
  // vilken typ av inlämning den precis startat, och guidar rubrik-val när det passar.
  const rawTyp = (searchParams.get("typ") || "").toLowerCase().trim();
  const ALLOWED_TYPS = new Set(["karta", "bygdepuls", "bild", "anslag", "evenemang"]);
  const typ = ALLOWED_TYPS.has(rawTyp) ? rawTyp : "";

  const TYP_HINTS = {
    karta: {
      title: "Du skickar ett karttips.",
      body:
        "Berätta gärna vad du sett på kartan – en plats som saknas, ett fel namn, fel läge, ett lokalt namn eller en detalj som bara någon med lokal kunskap känner till. Nämn gärna vilken kartrubrik det gäller: Folk & föreningar · Friluft & idrott · Hemma i byarna · Loppis & fika · Praktisk hjälp · Utflykter & historia · Vardag.",
    },
    bygdepuls: {
      title: "Du skickar ett tips till Bygdepuls.",
      body:
        "Något är på gång, växer fram eller är värt att fler känner till? Skriv en kort notis – vi lägger den bland de aktuella pulsnotiserna. Detta är inte ett Evenemang med fast tid, utan en liten nyhet från bygden.",
    },
    bild: {
      title: "Du skickar en bild från bygden.",
      body:
        "Använd formuläret för bild-inlämning (Din bild). Här på anslagssidan lägger vi vanliga anslag; bilder får en egen ingång så det syns tydligt i galleriet.",
    },
    anslag: {
      title: "Du skickar ett anslag.",
      body:
        "Ett anslag är en anzeige / mitteilung: du söker något, erbjuder något, vill fråga, bjuda in eller hjälpa. Är det istället ett konkret evenemang med datum och tid – välj Tipsa om evenemang i sidfoten.",
    },
    evenemang: {
      title: "Du skickar ett evenemang.",
      body:
        "Ett evenemang är en konkret händelse med datum, tid, plats och arrangör. Använd formuläret för evenemang så alla fält kommer med.",
    },
  };
  const typHint = typ ? TYP_HINTS[typ] : null;

  // Vid typ=anslag är default-rubriken lämpligen “Hjälp i vardagen” om ingen ?rubrik satt.
  // Vid typ=karta / bygdepuls lämnar vi rubrik tom så användaren själv väljer.
  const initialRubrik = preRubrik || (typ === "anslag" ? "Hjälp i vardagen" : "");

  const [form, setForm] = useState({
    rubrik: initialRubrik, titel: "", beskrivning: "", pristyp: "", prisbelopp: "",
    ort: "", adress: "", avsandare_namn: "", kontakt_epost: "", kontakt_telefon: "", samtycke: false,
    giltigt_tom: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [galleri, setGalleri] = useState([]); // array of {url, media_typ}
  const [uploadingImage, setUploadingImage] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (galleri.length >= 3) return;
    const isVideo = (file.type || "").startsWith("video/")
      || /\.(mp4|mov|webm|m4v)$/i.test(file.name || "");
    const maxBytes = isVideo ? 60 * 1024 * 1024 : 10 * 1024 * 1024;
    const maxLabel = isVideo ? "60 MB" : "10 MB";
    if (file.size > maxBytes) {
      setErrors(prev => ({ ...prev, submit: `${isVideo ? "Videon" : "Filen"} är för stor (max ${maxLabel}).` }));
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      setGalleri(prev => [...prev, { url: res.data.url, media_typ: res.data.media_typ || (isVideo ? "video" : "foto") }]);
      // Rensa eventuellt tidigare felmeddelande
      setErrors(prev => ({ ...prev, submit: null }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : "Kunde inte ladda upp filen. Försök igen eller välj en annan fil.";
      setErrors(prev => ({ ...prev, submit: msg }));
    }
    setUploadingImage(false);
    // Reset input så samma fil kan väljas igen efter borttagning
    if (e.target) e.target.value = "";
  };

  const removeImage = (idx) => setGalleri(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!form.rubrik) e.rubrik = "Välj en rubrik";
    if (!form.titel.trim()) e.titel = "Du behöver ange en titel";
    if (!form.beskrivning.trim()) e.beskrivning = "Du behöver ange en beskrivning";
    if (!form.avsandare_namn.trim()) e.avsandare_namn = "Du behöver ange ditt namn";
    if (!form.kontakt_epost.trim() && !form.kontakt_telefon.trim()) e.kontakt = "Ange e-post eller telefon";
    if (!form.samtycke) e.samtycke = "Du behöver godkänna villkoren för att skicka in";
    if (form.rubrik === "Köp & sälj lokalt" && !form.pristyp) e.pristyp = "Ange pristyp";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Scrolla tydligt till varningsrutan högst upp i formuläret
      setTimeout(() => {
        const banner = document.querySelector('[data-testid="form-error-banner"]');
        if (banner) banner.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/anslag/submit`, {
        rubrik: form.rubrik, titel: form.titel, beskrivning: form.beskrivning,
        pristyp: form.pristyp || null, prisbelopp: form.prisbelopp || null,
        ort: form.ort || null, adress: form.adress || null,
        bild_url: galleri[0]?.url || null,  // bakåtkompatibel
        galleri: galleri.length > 0 ? galleri.map(g => g.url) : null,
        avsandare_namn: form.avsandare_namn,
        kontakt_epost: form.kontakt_epost || null, kontakt_telefon: form.kontakt_telefon || null,
        giltigt_tom: form.giltigt_tom || null,
        samtycke: form.samtycke,
      });
      setSubmitted(true);
    } catch {
      setErrors({ submit: "Något gick fel. Försök igen." });
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="submission-confirmation" className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center sm:text-left">
          <CheckCircle size={48} className="text-pine mb-4 mx-auto sm:mx-0" />
          <h1 className="font-heading text-3xl font-semibold text-ink tracking-tight mb-3">Tack för ditt anslag!</h1>
          <p className="text-sm text-bark mb-2">Vi har tagit emot ditt anslag. Vårt lokala team granskar och publicerar det så snart som möjligt.</p>
          <p className="text-sm text-bark mb-8">Du behöver inte göra något mer.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/" data-testid="link-tillbaka-start" className="inline-flex items-center justify-center bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors no-underline">Tillbaka till startsidan</Link>
            <Link to="/anslagstavla" data-testid="link-tillbaka-anslagstavla" className="inline-flex items-center justify-center border border-pine text-pine hover:bg-bone-alt font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors no-underline">Visa anslagstavlan</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="skicka-anslag-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Skicka in</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3">Skicka in anslag</h1>
      <p className="text-sm text-bark mb-8 leading-relaxed">Alla är välkomna att skicka in &ndash; oavsett om du bor här, är på besök eller precis har hittat hit.</p>

      {/* Build 96.14 — Typ-kontext-banner: visar vilken CTA användaren startat med
          och guidar rätt fokus (karta / bygdepuls / bild / anslag / evenemang).
          Rent UI — ingen backend-ändring. */}
      {typHint && (
        <div
          data-testid={`typ-hint-${typ}`}
          className="mb-6 border border-pine/30 bg-pine/5 rounded-sm px-4 py-3 sm:px-5 sm:py-4"
        >
          <p className="font-heading text-base sm:text-lg font-semibold text-ink leading-tight">
            {typHint.title}
          </p>
          <p className="mt-1.5 text-sm text-bark/80 leading-relaxed">
            {typHint.body}
          </p>
          {/* Snabblänkar till dedikerade formulär när typen egentligen hör hemma där. */}
          {typ === "bild" && (
            <Link
              to="/bilder/skicka"
              data-testid="typ-hint-bild-link"
              className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline"
            >
              Öppna bild-formuläret →
            </Link>
          )}
          {typ === "evenemang" && (
            <Link
              to="/skicka-evenemang"
              data-testid="typ-hint-evenemang-link"
              className="inline-flex items-center gap-1.5 mt-2.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline"
            >
              Öppna evenemang-formuläret →
            </Link>
          )}
        </div>
      )}

      <AnslagGuideBox />

      <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
        {Object.keys(errors).filter(k => k !== "submit").length > 0 && (
          <div
            data-testid="form-error-banner"
            role="alert"
            aria-live="assertive"
            className="bg-falu/5 border border-falu/40 rounded-sm p-4 flex gap-3 items-start"
          >
            <span className="inline-flex shrink-0 w-6 h-6 rounded-full bg-falu text-parchment items-center justify-center text-sm font-bold leading-none">!</span>
            <div className="flex-1">
              <p className="font-heading text-base font-semibold text-falu mb-1 leading-snug">
                Några fält saknas
              </p>
              <p className="text-sm text-bark/80 mb-2 leading-relaxed">
                Fyll i alla fält som är markerade med <span className="text-falu font-bold">*</span> innan du skickar in. De markerade fälten nedan är inte ifyllda:
              </p>
              <ul className="list-disc pl-5 text-sm text-bark space-y-0.5">
                {Object.entries(errors).filter(([k]) => k !== "submit").map(([k, msg]) => (
                  <li key={k}><strong>{msg}</strong></li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <FieldGroup label="Rubrik" error={errors.rubrik} required>
          <select data-testid="field-rubrik" value={form.rubrik} onChange={e => set("rubrik", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none">
            <option value="">Välj rubrik...</option>
            {RUBRIKER.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </FieldGroup>

        <FieldGroup label="Titel på anslaget" help='T.ex. "Söker hjälp med vedkapning" eller "Säljer barnvagn"' error={errors.titel} required>
          <input data-testid="field-titel" type="text" value={form.titel} onChange={e => set("titel", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Beskrivning" help="Beskriv vad du erbjuder eller söker. Skriv gärna var du befinner dig." error={errors.beskrivning} required>
          <textarea data-testid="field-beskrivning" value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={4} className="w-full px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none resize-y" />
        </FieldGroup>

        {form.rubrik === "Köp & sälj lokalt" && (
          <>
            <FieldGroup label="Pristyp" error={errors.pristyp} required>
              <select data-testid="field-pristyp" value={form.pristyp} onChange={e => set("pristyp", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none">
                <option value="">Välj...</option>
                <option value="belopp">Ange pris</option>
                <option value="Skänkes">Skänkes</option>
                <option value="Bytes">Bytes</option>
                <option value="Överenskommelse">Överenskommelse</option>
              </select>
            </FieldGroup>
            {form.pristyp === "belopp" && (
              <FieldGroup label="Pris" help='T.ex. "500 kr"'>
                <input data-testid="field-prisbelopp" type="text" value={form.prisbelopp} onChange={e => set("prisbelopp", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
              </FieldGroup>
            )}
          </>
        )}

        <FieldGroup label="Ort / område" help="T.ex. Backe (Fjällsjö), Rossön (Bodum)">
          <input data-testid="field-ort" type="text" value={form.ort} onChange={e => set("ort", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Adress (frivilligt)" help="T.ex. Storgatan 5. Hjälper redaktionen att placera anslaget på kartan.">
          <input data-testid="field-adress" type="text" value={form.adress} onChange={e => set("adress", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label={`Bilder och video (${galleri.length} / 3)`} help="Valfritt. Upp till 3 filer. Bilder max 10 MB (JPG, PNG, WebP, GIF, HEIC). Video max 60 MB (MP4, MOV, WebM).">
          {galleri.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {galleri.map((item, i) => {
                const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
                const isVideo = item.media_typ === "video";
                return (
                  <div key={i} className="relative">
                    {isVideo ? (
                      <video
                        src={`${fullUrl}#t=0.5`}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-24 h-24 object-cover rounded-sm border border-warm-border bg-ink"
                        data-testid={`preview-video-${i}`}
                      />
                    ) : (
                      <img
                        src={fullUrl}
                        alt={`Bild ${i + 1}`}
                        className="w-24 h-24 object-cover rounded-sm border border-warm-border"
                        data-testid={`preview-bild-${i}`}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`remove-bild-${i}`}
                      className="absolute -top-2 -right-2 bg-falu hover:bg-falu-hover text-white rounded-full w-6 h-6 text-xs leading-none"
                      title="Ta bort"
                    >×</button>
                    {isVideo && (
                      <span className="absolute bottom-1 left-1 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-parchment/90 text-bark pointer-events-none">
                        Video
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {galleri.length < 3 && (
            <input
              data-testid="field-bild"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
              onChange={handleAddImage}
              disabled={uploadingImage}
              className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white"
            />
          )}
          {uploadingImage && (
            <p data-testid="upload-progress" className="text-sm text-bark/70 mt-1 italic">Laddar upp…</p>
          )}
          {galleri.length >= 3 && (
            <p className="text-sm text-bark/70 italic">Max 3 filer uppnådda. Ta bort en för att lägga till en annan.</p>
          )}
        </FieldGroup>

        <FieldGroup label="Giltigt till och med" help="Valfritt. Ange ett slutdatum om anslaget bara ska visas tills dess. Anslaget flyttas automatiskt till arkivet efter detta datum.">
          <input
            data-testid="field-giltigt-tom"
            type="date"
            value={form.giltigt_tom}
            onChange={e => set("giltigt_tom", e.target.value)}
            className="w-full sm:w-[280px] min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none"
          />
        </FieldGroup>

        <FieldGroup label="Ditt namn" help="Så att folk vet vem som ligger bakom anslaget." error={errors.avsandare_namn} required>
          <input data-testid="field-namn" type="text" value={form.avsandare_namn} onChange={e => set("avsandare_namn", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Kontaktväg" help="Din kontaktuppgift visas på anslaget så att intresserade kan nå dig. Ange e-post och/eller telefon." error={errors.kontakt}>
          <input data-testid="field-epost" type="email" placeholder="E-post" value={form.kontakt_epost} onChange={e => set("kontakt_epost", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none mb-3" />
          <input data-testid="field-telefon" type="tel" placeholder="Telefon" value={form.kontakt_telefon} onChange={e => set("kontakt_telefon", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <div className="flex items-start gap-3 mt-2">
          <input data-testid="field-samtycke" type="checkbox" checked={form.samtycke} onChange={e => set("samtycke", e.target.checked)} className="mt-1.5 w-5 h-5 accent-pine rounded" />
          <label className="text-base text-ink leading-relaxed">
            {/* Etappe 4.8 — uppdaterad bekräftelse-text med länkar till integritet + villkor-inskickat */}
            Jag bekräftar att jag har rätt att skicka in materialet och att vidalv.live får granska, redigera och publicera det enligt{" "}
            <Link to="/villkor-inskickat" target="_blank" className="text-falu underline">villkoren för inskickat material</Link> och{" "}
            <Link to="/integritet" target="_blank" className="text-falu underline">integritetspolicyn</Link>.
          </label>
        </div>
        {errors.samtycke && <p className="text-falu text-sm -mt-4">{errors.samtycke}</p>}

        {/* Etappe 4.8 — dezent dataskyddshinweis under formuläret */}
        <p data-testid="dataskydd-hint" className="text-xs text-bark/60 leading-relaxed -mt-2">
          Vi använder dina uppgifter för att kunna granska tipset, publicera relevant information
          och kontakta dig vid frågor. Läs mer i vår{" "}
          <Link to="/integritet" className="text-falu hover:text-falu-hover">integritetspolicy</Link>.
        </p>

        {errors.submit && <p className="text-falu text-base">{errors.submit}</p>}

        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-anslag-button"
          className="w-full sm:w-auto bg-falu hover:bg-falu-hover disabled:opacity-50 text-parchment font-body font-semibold text-sm px-6 py-3.5 rounded-sm transition-colors mt-2"
        >
          {submitting ? "Skickar..." : "Skicka in"}
        </button>
      </form>
    </div>
  );
}

function FieldGroup({ label, help, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-ink mb-1">
        {label}{required && <span className="text-falu ml-1">*</span>}
      </label>
      {help && <p className="text-xs text-bark mb-2">{help}</p>}
      {children}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
